import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, ROUTES } from './constants';
import { ApiResponse, ErrorResponse } from '@/types/api.types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true, // Enable cookies
    });

    this.setupInterceptors();
  }

  private isAuthRequest(url?: string): boolean {
    if (!url) return false;
    return url.includes('/auth/login') ||
           url.includes('/auth/logout') ||
           url.includes('/auth/profile') ||
           url.includes('/auth/verify-email') ||
           url.includes('/auth/resend-verification') ||
           url.includes('/auth/register') ||
           url.includes('/auth/forgot-password') ||
           url.includes('/auth/reset-password');
  }

  private setupInterceptors() {
    // Response interceptor - Handle errors and 304 responses
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ErrorResponse>) => {
        const requestUrl = error.config?.url;
        
        // Handle 304 Not Modified - return cached data structure
        if (error.response?.status === 304) {
          return {
            data: {
              success: true,
              message: 'Not Modified',
              data: {
                list: [],
                meta: {
                  total: 0,
                  page: '1',
                  limit: '10',
                  totalPages: 0
                }
              }
            },
            status: 304,
            statusText: 'Not Modified',
            headers: error.response.headers,
            config: error.config!
          };
        }
        
        // Only redirect on 401 if it's NOT an auth-related request
        if (error.response?.status === 401 && !this.isAuthRequest(requestUrl)) {
          localStorage.removeItem('auth_user');
          window.location.href = ROUTES.LOGIN;
        }
        
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url);
    return response.data;
  }
}

export const api = new ApiClient();
export default api;
